import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { TrendingUp, Shield, Gauge, Layers } from 'lucide-react';

const values = [
  {
    icon: Gauge,
    title: 'Performance',
    stat: '10x',
    description: 'Faster processing speeds through optimized architectures and intelligent caching strategies.'
  },
  {
    icon: Layers,
    title: 'Scalability',
    stat: '∞',
    description: 'Systems designed to handle exponential growth without performance degradation.'
  },
  {
    icon: Shield,
    title: 'Reliability',
    stat: '99.9%',
    description: 'Enterprise-grade uptime with redundant systems and automated failover.'
  },
  {
    icon: TrendingUp,
    title: 'Growth',
    stat: '3x',
    description: 'Average ROI improvement for clients within the first year of deployment.'
  }
];

const ValueCard = ({ value, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const Icon = value.icon;
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className="value-card group relative p-6 md:p-8 bg-white border border-slate-100 hover:border-[#003333]/20 transition-all duration-500 card-hover overflow-hidden"
      data-testid={`value-card-${index}`}
    >
      {/* Top row: Icon and Stat */}
      <div className="flex items-center justify-between mb-6">
        <div className="w-14 h-14 bg-[#F8FAFC] rounded-xl flex items-center justify-center text-[#003333] group-hover:bg-[#003333] group-hover:text-white transition-all duration-300">
          <Icon className="w-7 h-7" />
        </div>
        <span className="text-2xl md:text-3xl font-bold text-[#003333]/20 group-hover:text-[#003333]/30 transition-colors">
          {value.stat}
        </span>
      </div>
      
      <h3 className="text-xl md:text-2xl font-semibold tracking-tight text-[#003333] mb-3">
        {value.title}
      </h3>
      
      <p className="text-sm md:text-base leading-relaxed text-slate-600">
        {value.description}
      </p>
    </motion.div>
  );
};

export const ValuePropsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="py-24 md:py-32 bg-white" data-testid="value-props-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 md:mb-24"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">
            Why LOLCorp
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#003333] max-w-3xl mx-auto">
            Built for Enterprise Performance
          </h2>
        </motion.div>
        
        {/* Value Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => (
            <ValueCard key={value.title} value={value} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
